/**
 * MMEC surface / chart styling from ColorScheme — single source for analysis + chart TSX.
 * Replaces legacy isDarkMode + gray Tailwind splits.
 */
import { useMemo } from 'react';
import type { CSSProperties } from 'react';
import type { ColorScheme } from '../utils/colorScheme';
import { useTheme } from '../contexts/ThemeContext';

export type MmecFx = ReturnType<typeof createMmecFx>;

export function createMmecFx(colors: ColorScheme) {
  const border = `1px solid ${colors.surfaceBorder}`;
  const input: CSSProperties = {
    width: '100%',
    padding: '8px 12px',
    borderRadius: 6,
    border,
    backgroundColor: colors.surface,
    color: colors.text,
    boxSizing: 'border-box',
  };
  const inputXs: CSSProperties = {
    borderRadius: 4,
    border,
    backgroundColor: colors.surface,
    color: colors.text,
    fontSize: 11,
    padding: '2px 6px',
  };

  return {
    border,
    chartGrid: colors.surfaceBorder,
    chartAxis: colors.textMuted,
    overlayScrim: { backgroundColor: 'rgba(15, 23, 42, 0.55)' } as CSSProperties,
    root: {
      padding: 24,
      borderRadius: 12,
      boxShadow: '0 10px 15px -3px rgba(15,23,42,0.1)',
      backgroundColor: colors.surface,
      color: colors.text,
      border,
      ['--mmec-row-hover' as string]: colors.surfaceHover,
    } as CSSProperties,
    section: {
      padding: 16,
      borderRadius: 8,
      backgroundColor: colors.background,
      border,
      marginBottom: 24,
    } as CSSProperties,
    sectionMt: {
      padding: 16,
      borderRadius: 8,
      backgroundColor: colors.background,
      border,
      marginTop: 24,
    } as CSSProperties,
    nested: {
      padding: 16,
      borderRadius: 8,
      backgroundColor: colors.surface,
      border,
    } as CSSProperties,
    muted: { color: colors.textMuted } as CSSProperties,
    sub: { color: colors.textSecondary } as CSSProperties,
    label: {
      display: 'block',
      fontSize: 14,
      fontWeight: 500,
      marginBottom: 4,
      color: colors.textSecondary,
    } as CSSProperties,
    input,
    inputXs,
    hTitle: { fontSize: '1.5rem', fontWeight: 700, marginBottom: 16, color: colors.text } as CSSProperties,
    hSection: { fontSize: '1.125rem', fontWeight: 600, marginBottom: 16, color: colors.text } as CSSProperties,
    hBlock: { fontSize: '1.05rem', fontWeight: 600, marginBottom: 12, color: colors.text } as CSSProperties,
    hSmall: { fontSize: '1rem', fontWeight: 600, marginBottom: 8, color: colors.text } as CSSProperties,
    infoBanner: {
      padding: 16,
      borderRadius: 8,
      marginBottom: 16,
      border: `1px solid ${colors.chartPrimary}`,
      backgroundColor: colors.surfaceHover,
      color: colors.text,
    } as CSSProperties,
    accentBanner: {
      padding: 16,
      borderRadius: 8,
      marginBottom: 16,
      border: `1px solid ${colors.accent}`,
      backgroundColor: colors.surfaceHover,
      color: colors.text,
    } as CSSProperties,
    quoteOnly: {
      marginBottom: 12,
      fontSize: 14,
      borderRadius: 6,
      padding: '8px 12px',
      border: `1px solid ${colors.warning}`,
      backgroundColor: colors.surfaceHover,
      color: colors.text,
    } as CSSProperties,
    analysisHeader: {
      padding: 16,
      borderRadius: 8,
      border: `2px solid ${colors.surfaceBorder}`,
      backgroundColor: colors.surface,
    } as CSSProperties,
    pillBuy: {
      padding: '4px 12px',
      borderRadius: 9999,
      fontSize: 14,
      fontWeight: 600,
      backgroundColor: colors.surfaceHover,
      color: colors.chartSuccess,
      border: `1px solid ${colors.chartSuccess}`,
    } as CSSProperties,
    pillSell: {
      padding: '4px 12px',
      borderRadius: 9999,
      fontSize: 14,
      fontWeight: 600,
      backgroundColor: colors.surfaceHover,
      color: colors.chartError,
      border: `1px solid ${colors.chartError}`,
    } as CSSProperties,
    pillNeutral: {
      padding: '4px 12px',
      borderRadius: 9999,
      fontSize: 14,
      fontWeight: 600,
      backgroundColor: colors.surfaceHover,
      color: colors.textMuted,
      border,
    } as CSSProperties,
    pillEntry: {
      padding: '4px 12px',
      borderRadius: 9999,
      fontSize: 14,
      fontWeight: 600,
      backgroundColor: colors.surfaceHover,
      color: colors.chartPrimary,
      border: `1px solid ${colors.chartPrimary}`,
    } as CSSProperties,
    pillExit: {
      padding: '4px 12px',
      borderRadius: 9999,
      fontSize: 14,
      fontWeight: 600,
      backgroundColor: colors.surfaceHover,
      color: colors.chartWarning,
      border: `1px solid ${colors.chartWarning}`,
    } as CSSProperties,
    btnPrimary: {
      padding: '12px 24px',
      borderRadius: 8,
      fontWeight: 600,
      border: 'none',
      cursor: 'pointer',
      backgroundColor: colors.primary,
      color: colors.textInverse,
    } as CSSProperties,
    btnPrimaryDisabled: { opacity: 0.45, cursor: 'not-allowed' } as CSSProperties,
    btnGhost: {
      padding: '4px 12px',
      borderRadius: 6,
      fontSize: 13,
      fontWeight: 500,
      border,
      backgroundColor: colors.surfaceHover,
      color: colors.text,
      cursor: 'pointer',
    } as CSSProperties,
    btnGhostDisabled: { opacity: 0.45, cursor: 'not-allowed', backgroundColor: colors.surface } as CSSProperties,
    btnSuccess: {
      padding: '4px 12px',
      borderRadius: 6,
      fontSize: 13,
      fontWeight: 500,
      border: 'none',
      backgroundColor: colors.success,
      color: colors.textInverse,
      cursor: 'pointer',
    } as CSSProperties,
    errorBanner: {
      marginBottom: 16,
      padding: 16,
      borderRadius: 8,
      border: `1px solid ${colors.error}`,
      backgroundColor: colors.surface,
      color: colors.error,
    } as CSSProperties,
    tradesErrBox: {
      marginTop: 24,
      padding: 16,
      borderRadius: 8,
      border: `1px solid ${colors.error}`,
      backgroundColor: colors.surfaceHover,
      color: colors.error,
    } as CSSProperties,
    whaleWrap: {
      height: 384,
      padding: 16,
      borderRadius: 8,
      border,
      backgroundColor: colors.background,
    } as CSSProperties,
    whalePlot: {
      flex: 1,
      position: 'relative' as const,
      backgroundColor: colors.background,
      border,
      borderRadius: 8,
      height: 400,
    } as CSSProperties,
    chartLegend: { color: colors.textSecondary, fontSize: 14 } as CSSProperties,
    pos: { color: colors.chartSuccess } as CSSProperties,
    neg: { color: colors.chartError } as CSSProperties,
    callNet: { color: colors.boughtCalls } as CSSProperties,
    putNet: { color: colors.boughtPuts } as CSSProperties,
    premium: { color: colors.chartPrimary, fontWeight: 500 } as CSSProperties,
    th: {
      padding: '12px 16px',
      textAlign: 'left' as const,
      fontSize: 12,
      fontWeight: 500,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.05em',
      color: colors.textMuted,
      backgroundColor: colors.surfaceHover,
      borderBottom: border,
    } as CSSProperties,
    td: { padding: '12px 16px', fontSize: 14, color: colors.text, borderBottom: border } as CSSProperties,
    tdStrong: { padding: '12px 16px', fontSize: 14, fontWeight: 500, color: colors.text, borderBottom: border } as CSSProperties,
    table: { width: '100%', minWidth: '100%', borderCollapse: 'collapse' as const } as CSSProperties,
    filterRow: { marginBottom: 12, display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 8, color: colors.text } as CSSProperties,
    filterHint: { fontSize: 12, marginBottom: 8, color: colors.textMuted } as CSSProperties,
    hintUnderInput: { fontSize: 12, marginTop: 4, color: colors.textMuted } as CSSProperties,
    iconGreen: { color: colors.chartSuccess } as CSSProperties,
    iconRed: { color: colors.chartError } as CSSProperties,
    iconBlue: { color: colors.chartPrimary } as CSSProperties,
    panel: {
      padding: 16,
      borderRadius: 8,
      backgroundColor: colors.background,
      border: `1px solid ${colors.surfaceBorder}`,
    } as CSSProperties,
    panelMb: {
      padding: 16,
      borderRadius: 8,
      marginBottom: 24,
      backgroundColor: colors.background,
      border: `1px solid ${colors.surfaceBorder}`,
    } as CSSProperties,
    panelMt: {
      padding: 16,
      borderRadius: 8,
      marginTop: 24,
      backgroundColor: colors.background,
      border: `1px solid ${colors.surfaceBorder}`,
    } as CSSProperties,
    blueCallout: {
      padding: 16,
      borderRadius: 8,
      marginBottom: 16,
      border: `1px solid ${colors.chartPrimary}`,
      backgroundColor: colors.surfaceHover,
    } as CSSProperties,
    blueCalloutTitle: { fontSize: '1rem', fontWeight: 600, color: colors.chartPrimary } as CSSProperties,
    blueCalloutBody: { fontSize: 13, color: colors.textSecondary, marginTop: 8 } as CSSProperties,
    whaleChartShell: {
      height: '24rem',
      padding: 16,
      borderRadius: 8,
      border: `1px solid ${colors.surfaceBorder}`,
      backgroundColor: colors.surface,
    } as CSSProperties,
    tradeErrorBanner: {
      padding: 16,
      borderRadius: 8,
      marginTop: 24,
      border: `1px solid ${colors.error}`,
      backgroundColor: colors.surfaceHover,
      color: colors.error,
    } as CSSProperties,
    btnPage: {
      padding: '4px 12px',
      borderRadius: 6,
      fontSize: 13,
      fontWeight: 500,
      border: `1px solid ${colors.surfaceBorder}`,
      backgroundColor: colors.surfaceHover,
      color: colors.text,
      cursor: 'pointer',
    } as CSSProperties,
    btnPageDisabled: {
      padding: '4px 12px',
      borderRadius: 6,
      fontSize: 13,
      fontWeight: 500,
      border: `1px solid ${colors.surfaceBorder}`,
      backgroundColor: colors.surface,
      color: colors.textMuted,
      cursor: 'not-allowed',
    } as CSSProperties,
    btnAutoOn: {
      padding: '4px 12px',
      borderRadius: 6,
      fontSize: 13,
      fontWeight: 500,
      border: 'none',
      backgroundColor: colors.success,
      color: colors.textInverse,
      cursor: 'pointer',
    } as CSSProperties,
    selectTiny: {
      borderRadius: 4,
      border: `1px solid ${colors.surfaceBorder}`,
      backgroundColor: colors.surface,
      color: colors.text,
      fontSize: 11,
      padding: '4px 8px',
    } as CSSProperties,
    sidePillBuy: {
      padding: '2px 8px',
      borderRadius: 9999,
      fontSize: 12,
      fontWeight: 500,
      backgroundColor: colors.surfaceHover,
      color: colors.chartSuccess,
      border: `1px solid ${colors.chartSuccess}`,
    } as CSSProperties,
    sidePillSell: {
      padding: '2px 8px',
      borderRadius: 9999,
      fontSize: 12,
      fontWeight: 500,
      backgroundColor: colors.surfaceHover,
      color: colors.chartError,
      border: `1px solid ${colors.chartError}`,
    } as CSSProperties,
    sidePillMid: {
      padding: '2px 8px',
      borderRadius: 9999,
      fontSize: 12,
      fontWeight: 500,
      backgroundColor: colors.surfaceHover,
      color: colors.textMuted,
      border: `1px solid ${colors.surfaceBorder}`,
    } as CSSProperties,
    /** Recharts / HTML tooltips */
    tooltipCard: {
      padding: 12,
      borderRadius: 8,
      border: `1px solid ${colors.surfaceBorder}`,
      backgroundColor: colors.surface,
      color: colors.text,
      boxShadow: '0 10px 15px -3px rgba(15,23,42,0.12)',
    } as CSSProperties,
    tooltipTitle: { fontWeight: 600, marginBottom: 8, color: colors.text } as CSSProperties,
    tooltipMuted: { color: colors.textMuted, fontSize: 12 } as CSSProperties,
  };
}

export function useMmecFx(): MmecFx {
  const { colors } = useTheme();
  return useMemo(() => createMmecFx(colors), [colors]);
}
